import Accordion from "@/components/Accordion";
import React from "react";
import LogoUpload from "../../LogoUpload";
import ColorPicker from "@/components/ColorPicker";
import Slider from "@/components/Slider";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { Checkbox, Toggle } from "@velaro/velaro-component-lib";
import { ChatWindow } from "../../types";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
  setStagedLogoFile: (file: File | null) => void;
}

type PillOption = { value: string; label: string };

function PillPicker({
  options,
  value,
  onChange
}: {
  options: PillOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isSelected = value === opt.value;
        return (
          <div
            key={opt.value}
            className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
              isSelected
                ? "border-primary bg-primary-bg text-primary-active"
                : "border-slate-300 bg-white text-slate-600"
            }`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </div>
        );
      })}
    </div>
  );
}

export function Branding({
  onExpanded,
  workingChatWindow,
  setWorkingChatWindow,
  setStagedLogoFile
}: Props) {
  const workingStyleConfig = workingChatWindow.styleConfig || {};

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    setWorkingChatWindow({
      ...workingChatWindow,
      styleConfig: { ...workingChatWindow.styleConfig, ...updates }
    });
  };

  const darkMode = workingStyleConfig.darkMode ?? "off";

  return (
    <Accordion.Item eventKey="branding" onExpanded={() => onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Branding</Accordion.Title>
        <Accordion.SubTitle>
          Customize the chat window to reflect your brand identity
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="space-y-5">

          {/* Logo */}
          <LogoUpload
            setStageLogoFile={setStagedLogoFile}
            update={updateStyleConfig}
            workingStyleConfig={workingStyleConfig}
            field="logoUrl"
            label="Logo"
            previewBackground={workingStyleConfig.backgroundColor}
            sizeGuidance="PNG or SVG • Transparent background recommended • Any size accepted"
          />

          <div className="border-t border-slate-200" />

          {/* Dark Mode */}
          <div>
            <div className="text-sm font-medium mb-2">Dark Mode</div>
            <PillPicker
              options={[
                { value: "off", label: "Off" },
                { value: "on", label: "Always on" },
                { value: "auto", label: "Follow visitor OS" },
              ]}
              value={darkMode}
              onChange={(v) => updateStyleConfig({ darkMode: v })}
            />
            {darkMode !== "off" && (
              <div className="mt-3 pl-3 border-l-2 border-slate-200 space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Dark mode color overrides</div>
                <div className="grid grid-cols-4 gap-2 items-center text-xs text-slate-400 mb-1">
                  <div />
                  <div>Background</div>
                  <div>Text</div>
                  <div />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <div className="text-sm font-normal">Frame</div>
                  <ColorPicker
                    value={workingStyleConfig.darkFrameBackgroundColor ?? "#1E1E2E"}
                    onChange={(v) => updateStyleConfig({ darkFrameBackgroundColor: v })}
                  />
                  <ColorPicker
                    value={workingStyleConfig.darkFrameTextColor ?? "#CDD6F4"}
                    onChange={(v) => updateStyleConfig({ darkFrameTextColor: v })}
                  />
                  <div />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <div className="text-sm font-normal">Agent bubble</div>
                  <ColorPicker
                    value={workingStyleConfig.darkAgentBubbleBgColor ?? "#313244"}
                    onChange={(v) => updateStyleConfig({ darkAgentBubbleBgColor: v })}
                  />
                  <ColorPicker
                    value={workingStyleConfig.darkAgentBubbleTextColor ?? "#CDD6F4"}
                    onChange={(v) => updateStyleConfig({ darkAgentBubbleTextColor: v })}
                  />
                  <div />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <div className="text-sm font-normal">Visitor bubble</div>
                  <ColorPicker
                    value={workingStyleConfig.darkVisitorBubbleBgColor ?? workingStyleConfig.backgroundColor ?? "#1670E7"}
                    onChange={(v) => updateStyleConfig({ darkVisitorBubbleBgColor: v })}
                  />
                  <ColorPicker
                    value={workingStyleConfig.darkVisitorBubbleTextColor ?? "#FFFFFF"}
                    onChange={(v) => updateStyleConfig({ darkVisitorBubbleTextColor: v })}
                  />
                  <div />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200" />

          {/* Colors */}
          <div>
            <div className="text-sm font-medium mb-2">Color</div>
            <div className="grid grid-cols-3 items-center gap-4 mb-3">
              <div className="text-sm font-normal">Primary Color</div>
              <ColorPicker
                value={workingStyleConfig.backgroundColor}
                onChange={(value) => updateStyleConfig({ backgroundColor: value })}
              />
              <div />
            </div>
            <div className="grid grid-cols-3 items-center gap-4 mb-3">
              <div className="text-sm font-normal">Header Text Color</div>
              <ColorPicker
                value={workingStyleConfig.headerTextColor || ""}
                onChange={(value) => updateStyleConfig({ headerTextColor: value })}
                disabled={workingStyleConfig.useContrastedColorHeader}
              />
              <Checkbox
                checked={workingStyleConfig.useContrastedColorHeader}
                onClick={() =>
                  updateStyleConfig({ useContrastedColorHeader: !workingStyleConfig.useContrastedColorHeader })
                }
                label="Use Contrast Color"
              />
            </div>
            <div className="grid grid-cols-3 items-center gap-4 mb-3">
              <div className="text-sm font-normal">Body Text Color</div>
              <ColorPicker
                value={workingStyleConfig.bodyTextColor || ""}
                onChange={(value) => updateStyleConfig({ bodyTextColor: value })}
                disabled={workingStyleConfig.useContrastedColorBody}
              />
              <Checkbox
                checked={workingStyleConfig.useContrastedColorBody}
                onClick={() =>
                  updateStyleConfig({ useContrastedColorBody: !workingStyleConfig.useContrastedColorBody })
                }
                label="Use Contrast Color"
              />
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Font */}
          <div>
            <div className="text-sm font-medium mb-2">Font</div>
            <select
              className="border border-slate-300 rounded-md p-2 w-full text-sm"
              value={workingStyleConfig.fontFamily ?? "inherit"}
              onChange={(e) => updateStyleConfig({ fontFamily: e.target.value })}
            >
              <option value="inherit">Default (inherit from page)</option>
              <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
              <option value="'Inter', sans-serif">Inter</option>
              <option value="'Roboto', sans-serif">Roboto</option>
              <option value="'Open Sans', sans-serif">Open Sans</option>
              <option value="'Lato', sans-serif">Lato</option>
              <option value="'Poppins', sans-serif">Poppins</option>
              <option value="'Nunito', sans-serif">Nunito</option>
              <option value="Arial, sans-serif">Arial</option>
              <option value="Georgia, serif">Georgia</option>
            </select>
          </div>

          <div className="border-t border-slate-200" />

          {/* Frame */}
          <div>
            <div className="text-sm font-medium mb-2">Frame</div>
            <div className="grid grid-cols-3 items-center gap-4 mb-3">
              <div className="text-sm font-normal">Frame Background</div>
              <ColorPicker
                value={workingStyleConfig.frameBackgroundColor ?? "#EBEBEB"}
                onChange={(value) => updateStyleConfig({ frameBackgroundColor: value })}
              />
              <div />
            </div>
            <div className="grid grid-cols-3 items-center gap-4 mb-3">
              <div className="text-sm font-normal">Frame Text Color</div>
              <ColorPicker
                value={workingStyleConfig.frameTextColor ?? "#2F3742"}
                onChange={(value) => updateStyleConfig({ frameTextColor: value })}
              />
              <div />
            </div>
            <div className="flex justify-between items-center mt-2">
              <div className="text-sm font-normal">Enable Popout Button</div>
              <Toggle
                toggled={workingStyleConfig.enablePopout ?? false}
                changeToggle={() =>
                  updateStyleConfig({ enablePopout: !(workingStyleConfig.enablePopout ?? false) })
                }
              />
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Chat Bubbles */}
          <div>
            <div className="text-sm font-medium mb-3">Chat Bubbles</div>
            <div className="grid grid-cols-4 gap-2 items-center text-xs text-slate-400 mb-1">
              <div />
              <div>Background</div>
              <div>Text</div>
              <div>Border</div>
            </div>
            <div className="grid grid-cols-4 items-center gap-2 mb-3">
              <div className="text-sm font-normal">Agent</div>
              <ColorPicker value={workingStyleConfig.agentBubbleBgColor ?? "#FFFFFF"} onChange={(v) => updateStyleConfig({ agentBubbleBgColor: v })} />
              <ColorPicker value={workingStyleConfig.agentBubbleTextColor ?? "#161616"} onChange={(v) => updateStyleConfig({ agentBubbleTextColor: v })} />
              <ColorPicker value={workingStyleConfig.agentBubbleBorderColor ?? "#E1E1E1"} onChange={(v) => updateStyleConfig({ agentBubbleBorderColor: v })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-2 mb-3">
              <div className="text-sm font-normal">Visitor</div>
              <ColorPicker value={workingStyleConfig.visitorBubbleBgColor ?? ""} onChange={(v) => updateStyleConfig({ visitorBubbleBgColor: v })} />
              <ColorPicker value={workingStyleConfig.visitorBubbleTextColor ?? ""} onChange={(v) => updateStyleConfig({ visitorBubbleTextColor: v })} />
              <ColorPicker value={workingStyleConfig.visitorBubbleBorderColor ?? ""} onChange={(v) => updateStyleConfig({ visitorBubbleBorderColor: v })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-2 mb-4">
              <div className="text-sm font-normal">System Text</div>
              <ColorPicker value={workingStyleConfig.systemTextColor ?? "#777470"} onChange={(v) => updateStyleConfig({ systemTextColor: v })} />
              <div /><div />
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium mb-2">Visitor Bubble Side</div>
                <PillPicker
                  options={[{ value: "right", label: "Right" }, { value: "left", label: "Left" }]}
                  value={workingStyleConfig.visitorBubbleAlignment ?? "right"}
                  onChange={(v) => updateStyleConfig({ visitorBubbleAlignment: v })}
                />
              </div>

              <div>
                <div className="text-sm font-medium mb-2">Agent Avatar Shape</div>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: "circle", label: "Circle", radius: "50%" },
                    { value: "rounded", label: "Rounded", radius: "6px" },
                    { value: "square", label: "Square", radius: "0px" },
                  ].map((opt) => {
                    const isSelected = (workingStyleConfig.agentAvatarShape ?? "circle") === opt.value;
                    return (
                      <div
                        key={opt.value}
                        className={`flex flex-col items-center gap-1.5 cursor-pointer p-2 rounded-lg border-2 ${isSelected ? "border-primary bg-primary-bg" : "border-slate-200 bg-white"}`}
                        onClick={() => updateStyleConfig({ agentAvatarShape: opt.value })}
                      >
                        <div
                          className="w-8 h-8 bg-slate-400"
                          style={{ borderRadius: opt.radius }}
                        />
                        <span className={`text-xs ${isSelected ? "text-primary-active font-medium" : "text-slate-500"}`}>{opt.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="text-sm font-normal">Show agent name above messages</div>
                  <Toggle
                    toggled={workingStyleConfig.showAgentNameAboveBubbles ?? true}
                    changeToggle={() => updateStyleConfig({ showAgentNameAboveBubbles: !(workingStyleConfig.showAgentNameAboveBubbles ?? true) })}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-normal">Show visitor name above messages</div>
                  <Toggle
                    toggled={workingStyleConfig.showVisitorNameInBubbles ?? false}
                    changeToggle={() => updateStyleConfig({ showVisitorNameInBubbles: !workingStyleConfig.showVisitorNameInBubbles })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Button Styles */}
          <div>
            <div className="text-sm font-medium mb-2">Button Styles</div>
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm font-normal">Button Radius</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.buttonRadius ?? 6}
                  onChange={(e) => {
                    let value = parseInt(e.target.value);
                    if (isNaN(value)) value = 6;
                    if (value > 24) value = 24;
                    if (value < 0) value = 0;
                    updateStyleConfig({ buttonRadius: value });
                  }}
                  min={0}
                  max={24}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              min={0}
              max={24}
              value={workingStyleConfig.buttonRadius ?? 6}
              onChange={(value) => updateStyleConfig({ buttonRadius: value })}
            />

            {/* Aligned 4-column grid: label | bg | text | border */}
            <div className="grid grid-cols-4 gap-2 items-center text-xs text-slate-400 mt-3 mb-1">
              <div />
              <div>Background</div>
              <div>Text</div>
              <div>Border</div>
            </div>
            <div className="grid grid-cols-4 items-center gap-2 mb-2">
              <div className="text-sm font-normal">Primary</div>
              <ColorPicker value={workingStyleConfig.primaryBtnBgColor ?? "#1670E7"} onChange={(v) => updateStyleConfig({ primaryBtnBgColor: v })} />
              <ColorPicker value={workingStyleConfig.primaryBtnTextColor ?? "#FFFFFF"} onChange={(v) => updateStyleConfig({ primaryBtnTextColor: v })} />
              <div className="text-xs text-slate-300">—</div>
            </div>
            <div className="grid grid-cols-4 items-center gap-2 mb-3">
              <div className="text-sm font-normal">Secondary</div>
              <ColorPicker value={workingStyleConfig.secondaryBtnBgColor ?? "#FFFFFF"} onChange={(v) => updateStyleConfig({ secondaryBtnBgColor: v })} />
              <ColorPicker value={workingStyleConfig.secondaryBtnTextColor ?? "#64748B"} onChange={(v) => updateStyleConfig({ secondaryBtnTextColor: v })} />
              <ColorPicker value={workingStyleConfig.secondaryBtnBorderColor ?? ""} onChange={(v) => updateStyleConfig({ secondaryBtnBorderColor: v })} />
            </div>
          </div>

        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
